"use client"

import React from "react"
import type { SearchResult } from "@/schema/search"
import { Badge, Command, CommandEmpty, CommandGroup, CommandItem, CommandList } from "@ui/shad"

import { useGlobalSearch } from "@/hooks/use-global-search"
import { CustomDialog } from "@/components/dialogs"

import { PreviewPane } from "./command-palette/preview-pane"
import { SearchBar } from "./command-palette/search-bar"
import { getRecordTypeLabel, resultKey } from "./command-palette/utils"

interface SearchDialogProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  onSelect?: (item: SearchResult) => void
  title?: string
  placeholder?: string
  allowedTypes?: SearchResult["type"][]
  bookIdOverride?: string
  showPreview?: boolean
  widthVariant?: "default" | "compact"
}

export function SearchDialog({
  open,
  onOpenChange,
  onSelect,
  title = "Search",
  placeholder = "Type to search...",
  allowedTypes,
  bookIdOverride,
  showPreview = true,
  widthVariant = "default",
}: SearchDialogProps) {
  const { searchQuery, results, isSearching, handleSearchChange, clearSearch } = useGlobalSearch({
    allowedTypes,
    bookIdOverride,
  })
  const [selectedItem, setSelectedItem] = React.useState<SearchResult>()
  const [commandValue, setCommandValue] = React.useState<string>("")

  const hasResults = results.length > 0
  const hasSearchQuery = searchQuery.trim().length > 0

  React.useEffect(() => {
    if (!open) {
      clearSearch()
      setSelectedItem(undefined)
      setCommandValue("")
    }
  }, [open, clearSearch])

  React.useEffect(() => {
    if (results.length > 0 && !selectedItem) {
      const firstResult = results[0]
      setCommandValue(resultKey(firstResult))
      setSelectedItem(firstResult)
    }
  }, [results, selectedItem])

  React.useEffect(() => {
    if (commandValue) {
      const record = results.find((r) => resultKey(r) === commandValue)
      if (record) {
        setSelectedItem(record)
      }
    }
  }, [commandValue, results])

  const handleItemSelect = React.useCallback(
    (item: SearchResult) => {
      onSelect?.(item)
      onOpenChange(false)
    },
    [onSelect, onOpenChange]
  )

  const dialogWidthClass = React.useMemo(() => {
    if (widthVariant === "compact") {
      return "max-w-xl p-0"
    }

    return showPreview ? "max-w-3xl p-0" : "max-w-2xl p-0"
  }, [showPreview, widthVariant])

  return (
    <CustomDialog open={open} onOpenChange={onOpenChange} title={title} customClassName={dialogWidthClass}>
      <div className="flex">
        <div className={showPreview ? "flex-1 border-r" : "flex-1"}>
          <Command
            shouldFilter={false}
            value={commandValue}
            onValueChange={setCommandValue}
            onKeyDown={(e) => {
              if (e.key === "Enter" && selectedItem) {
                e.preventDefault()
                handleItemSelect(selectedItem)
              }
            }}
          >
            <SearchBar
              searchQuery={searchQuery}
              isSearching={isSearching}
              onSearchChange={handleSearchChange}
              placeholder={placeholder}
              autoFocus={open}
            />
            <CommandList className="h-full max-h-[400px]">
              {hasSearchQuery && !hasResults && !isSearching && <CommandEmpty>No results found.</CommandEmpty>}
              {!hasSearchQuery && <CommandEmpty>{placeholder}</CommandEmpty>}

              {hasResults && (
                <CommandGroup heading="Search Results">
                  {results.map((result) => {
                    const key = resultKey(result)
                    return (
                      <CommandItem key={key} value={key} onSelect={() => handleItemSelect(result)}>
                        <div className="flex w-full items-center justify-between">
                          <span className="truncate">{result.display_label || "No label"}</span>
                          <Badge variant="outline" size="sm" className="shrink-0">
                            {getRecordTypeLabel(result.type)}
                          </Badge>
                        </div>
                      </CommandItem>
                    )
                  })}
                </CommandGroup>
              )}
            </CommandList>
          </Command>
        </div>
        {showPreview && selectedItem && (
          <PreviewPane selectedItem={selectedItem} onNavigate={() => onOpenChange(false)} />
        )}
      </div>
    </CustomDialog>
  )
}
